import { useAuth0 } from '@auth0/auth0-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE;

export function useApi() {
  const { getAccessTokenSilently } = useAuth0();

  async function apiFetch(path, options = {}) {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: AUDIENCE },
    });

    const isFormData = options.body instanceof FormData;

    const response = await fetch(`${API_BASE}/api${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      // 9/23/26 — was discarding the backend's real error message entirely
      // (errorResponse's body is {"error": "<real reason>"}, e.g. "Open Play
      // is full for this session (8/8 spots reserved)"). Every caller only
      // ever saw a generic "API error: 500 Internal Server Error" no matter
      // what actually went wrong, so any UI trying to show e.message to the
      // user showed something useless. Fall back to the generic message
      // only when the body isn't the expected JSON shape.
      let message = `API error: ${response.status} ${response.statusText}`;
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch { /* not JSON, or empty body — keep the generic message */ }
      throw new Error(message);
    }

    return response.json();
  }

  const get = (path) => apiFetch(path, { method: 'GET' });
  const post = (path, body) => apiFetch(path, {
    method: 'POST',
    body: body instanceof FormData ? body : JSON.stringify(body),
  });
  const put = (path, body) => apiFetch(path, { method: 'PUT', body: JSON.stringify(body) });

  // Raw file download (PDF etc.) with the same sign-in; returns a Blob.
  const getBlob = async (path) => {
    const token = await getAccessTokenSilently({ authorizationParams: { audience: AUDIENCE } });
    const response = await fetch(`${API_BASE}/api${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) throw new Error(`Download failed (${response.status})`);
    return response.blob();
  };

  return { get, post, put, apiFetch, getBlob };
}
